import { useTranslation } from 'react-i18next';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Calendar as CalendarComponent } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { 
  Calendar, 
  ChevronLeft, 
  ChevronRight, 
  Loader2, 
  Home, 
  CalendarIcon,
  Download,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, startOfWeek, endOfWeek, eachDayOfInterval, isWeekend, startOfMonth, endOfMonth, addMonths } from 'date-fns';
import { exportUnifiedView, exportUnifiedOverviewPDF } from '../shared/ExcelExport';
import { getAssignmentsForDate } from '../../lib/projectAssignments';
import { API, formatDateDDMMYYYY, getWeekNumber, generateWeekOptions } from './utils/helpers';

const UnifiedOverviewTab = ({ projects, employees }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportRange, setExportRange] = useState({ startDate: '', endDate: '' });
  const [exportLoading, setExportLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekDays = useMemo(() => eachDayOfInterval({ start: weekStart, end: weekEnd }), [weekStart, weekEnd]);
  const weekNumber = useMemo(() => getWeekNumber(currentDate), [currentDate]);

  const weekOptions = useMemo(() => generateWeekOptions(currentDate), [currentDate]);
  const currentWeekValue = useMemo(() => format(weekStart, 'yyyy-MM-dd'), [weekStart]);

  // Fetch company info on mount
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await axios.get(`${API}/company/profile`);
        setCompanyInfo(response.data);
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };
    fetchCompanyInfo();
  }, []);

  const navigateWeek = (direction) => {
    setCurrentDate(prev => addDays(prev, direction * 7));
  };

  const goToCurrentWeek = () => {
    setCurrentDate(new Date());
  };

  const handleWeekDropdownChange = (value) => {
    setCurrentDate(new Date(value));
  };

  const handleDateSelect = (date) => {
    if (date) {
      setCurrentDate(date);
      setDatePickerOpen(false);
    }
  };

  // Availability now comes from project team-member assignments
  // (source of truth). Manpower Plans are draft proposals and no
  // longer feed the Unified view.
  const getDayAssignments = useCallback((date) => {
    return getAssignmentsForDate(projects, employees, date);
  }, [projects, employees]);

  // Export Unified View to Excel
  const handleExportUnified = async () => {
    setExportLoading(true);
    try {
      await exportUnifiedView({
        projects,
        employees,
        startDate: exportRange.startDate,
        endDate: exportRange.endDate,
        companyInfo
      });
      
      toast.success(t('auto.ctr.excelFileDownloaded'));
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error(t('auto.ctr.failedToExport'));
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportUnifiedPDF = async () => {
    setExportLoading(true);
    try {
      await exportUnifiedOverviewPDF({
        projects,
        employees,
        startDate: exportRange.startDate,
        endDate: exportRange.endDate,
        companyInfo
      });
      
      toast.success(t('auto.ctr.pdfFileDownloaded'));
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(t('auto.ctr.failedToExportPdf'));
    } finally {
      setExportLoading(false);
    }
  };

  // Open export dialog
  const openExportDialog = () => {
    setExportRange({
      startDate: format(weekStart, 'yyyy-MM-dd'),
      endDate: format(weekEnd, 'yyyy-MM-dd')
    });
    setExportDialogOpen(true);
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                {t('auto.ctr.unifiedManpowerOverview')}
              </CardTitle>
              <CardDescription>
                Week {weekNumber} • {formatDateDDMMYYYY(weekStart)} - {formatDateDDMMYYYY(weekEnd)}
              </CardDescription>
            </div>
            {/* Navigation Controls */}
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)} title={t('auto.ctr.previousWeek')}>
                <ChevronLeft className="h-4 w-4 mr-1" /> {t('auto.ctr.prev')}
              </Button>
              
              {/* Week Dropdown */}
              <Select value={currentWeekValue} onValueChange={handleWeekDropdownChange}>
                <SelectTrigger className="w-[280px]">
                  <SelectValue placeholder={t('auto.ctr.selectAWeek')} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {weekOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Date Picker */}
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm">
                    <CalendarIcon className="h-4 w-4 mr-1" />
                    {t('auto.ctr.pickDate')}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <CalendarComponent
                    mode="single"
                    selected={currentDate}
                    onSelect={handleDateSelect}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} title={t('auto.ctr.nextWeek')} data-testid="btn-next">
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
              
              <Button variant="outline" size="sm" onClick={goToCurrentWeek} title={t('auto.ctr.currentWeek')} data-testid="btn-gotocurrentweek">
                <Home className="h-4 w-4" />
              </Button>

              {/* Export to Excel */}
              <div className="border-l pl-2 ml-1">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={openExportDialog}
                  className="bg-green-50 hover:bg-green-100 border-green-200 text-green-700"
                  title={t('auto.ctr.exportToExcel')}
                  data-testid="export-unified-btn"
                >
                  <Download className="h-4 w-4 mr-1" /> {t('auto.ctr.export')}
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Mobile scrollable container */}
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <div className="min-w-[800px]">
              <div className="grid grid-cols-7 gap-2">
                {weekDays.map(day => {
                  const assignments = getDayAssignments(day);
                  const weekend = isWeekend(day);
                  
                  return (
                    <div 
                      key={day.toISOString()}
                      className={`border rounded-lg p-2 min-h-[150px] ${weekend ? 'bg-gray-50' : ''}`}
                    >
                      <div className="text-center mb-2 pb-2 border-b">
                        <div className="text-xs text-gray-500">{format(day, 'EEE')}</div>
                        <div className="font-medium">{formatDateDDMMYYYY(day)}</div>
                      </div>
                      <div className="space-y-1">
                        {assignments.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center">{t('auto.ctr.noAssignments')}</p>
                        ) : (
                          assignments.slice(0, 5).map((a, idx) => (
                            <div key={idx} className="text-xs p-1 bg-blue-50 rounded truncate" title={`${a.employee_name} - ${a.project_name}`}>
                              <span className="font-medium">{a.employee_name}</span>
                              <span className="text-gray-500"> • {a.project_name}</span>
                            </div>
                          ))
                        )}
                        {assignments.length > 5 && (
                          <p className="text-xs text-gray-500 text-center">+{assignments.length - 5} more</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auto.ctr.exportUnifiedOverview')}</DialogTitle>
            <DialogDescription>
              Export project team assignments per employee across the selected range. Days with an assignment show a symbol (see legend); empty cells mean the employee has no project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('auto.ctr.startDate2')}</Label>
                <Input
                  type="date"
                  value={exportRange.startDate}
                  onChange={(e) => setExportRange(prev => ({ ...prev, startDate: e.target.value  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>{t('auto.ctr.endDate2')}</Label>
                <Input
                  type="date"
                  value={exportRange.endDate}
                  onChange={(e) => setExportRange(prev => ({ ...prev, endDate: e.target.value  }))}
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={() => {
                setExportRange({
                  startDate: format(weekStart, 'yyyy-MM-dd'),
                  endDate: format(weekEnd, 'yyyy-MM-dd')
                });
              }} data-testid="btn-current-week">{t('auto.ctr.currentWeek2')}</Button>
              <Button variant="outline" size="sm" onClick={() => {
                const now = new Date();
                setExportRange({
                  startDate: format(startOfMonth(now), 'yyyy-MM-dd'),
                  endDate: format(endOfMonth(now), 'yyyy-MM-dd')
                });
              }} data-testid="btn-current-month">{t('auto.ctr.currentMonth')}</Button>
              <Button variant="outline" size="sm" onClick={() => {
                const now = new Date();
                const start = startOfMonth(now);
                setExportRange({
                  startDate: format(start, 'yyyy-MM-dd'),
                  endDate: format(endOfMonth(addMonths(start, 2)), 'yyyy-MM-dd')
                });
              }} data-testid="btn-next-3-months">{t('auto.ctr.next3Months')}</Button>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setExportDialogOpen(false)} data-testid="dialog-cancel-btn">{t('auto.ctr.cancel')}</Button>
            <Button onClick={handleExportUnifiedPDF} disabled={exportLoading} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50" data-testid="dialog-exportunifiedpdf-btn">
              {exportLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
              Export to PDF
            </Button>
            <Button onClick={handleExportUnified} disabled={exportLoading} className="bg-green-600 hover:bg-green-700" data-testid="dialog-exportunified-btn">
              {exportLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
              Export to Excel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedOverviewTab;
